import { api } from 'services/api/api.rtk';
import { makeSelectAuthApiConfigWithStore } from 'services/api/api.selectors';
import {
  getLimitationPendingSuccessResponseSchema,
  transformAccountLimitationPendingResponse,
  TransformedAccountLimitationPendingResponse,
} from './get-account-limitation-pending';
import {
  accountLimitationTypesResponseSchema,
  transformAccountLimitationsResponse,
  TransformedAccountLimitationResponse,
} from './get-account-limitation-types';
import {
  AutomaticWithdrawalLimitIResponse,
  automaticWithdrawalLimitResponseSchema,
} from './get-automatic-withdrawal-limit';
import {
  gamblingLimitationDetailsResponseSchema,
  TransformedGamblingLimitationDetailsResponse,
  transformGamblingLimitationDetailsResponse,
} from './get-gambling-loss-limitation-details';
import {
  lossLimitationTypesResponseSchema,
  TransformedLossLimitationTypesResponse,
  transformLossLimitationTypesResponse,
} from './get-loss-limitation-types';
import {
  selfExclusionResponseSchema,
  TransformedSelfExclusionResponse,
  transformSelfExclusionResponse,
} from './get-self-exclusion';
import { SelfExclusionTypesIResponse } from './get-self-exclusion-types';
import {
  sessionLossDetailResponseSchema,
  TransformedSessionLossDetailResponse,
  transformSessionLossDetailResponse,
} from './get-session-loss-detail';
import {
  topUpLimitationResponseSchema,
  TransformedTopUpLimitationResponse,
  transformTopUpLimitationResponse,
} from './get-top-up-limitation';
import {
  AccountLimitationIResponse,
  AccountLimitationRequest,
  transformAccountLimitationErrorResponse,
} from './post-self-limitation-topup-limitation';

const enhancedApi = api.injectEndpoints({
  endpoints: (build) => ({
    accountLimitationTypes: build.query<
      TransformedAccountLimitationResponse,
      void
    >({
      query: () =>
        makeSelectAuthApiConfigWithStore({
          url: '/responsible-gaming/v1/self-limitation/topup/type',
          method: 'GET',
          enrichRequestData: false,
        }),
      transformResponse: transformAccountLimitationsResponse,
      rawResponseSchema: accountLimitationTypesResponseSchema,
    }),
    accountLimitationPending: build.query<
      TransformedAccountLimitationPendingResponse,
      void
    >({
      query: () =>
        makeSelectAuthApiConfigWithStore({
          url: '/responsible-gaming/v1/self-limitation/topup/pending',
          method: 'GET',
          enrichRequestData: false,
        }),
      transformResponse: transformAccountLimitationPendingResponse,
      rawResponseSchema: getLimitationPendingSuccessResponseSchema,
    }),
    automaticWithdrawalLimit: build.query<
      AutomaticWithdrawalLimitIResponse,
      void
    >({
      query: () =>
        makeSelectAuthApiConfigWithStore({
          url: '/responsible-gaming/v1/self-limitation/automatic-withdrawal',
          method: 'GET',
          enrichRequestData: false,
        }),
      responseSchema: automaticWithdrawalLimitResponseSchema,
    }),
    gamblingLimitationDetails: build.query<
      TransformedGamblingLimitationDetailsResponse,
      void
    >({
      query: () =>
        makeSelectAuthApiConfigWithStore({
          url: '/responsible-gaming/v1/self-limitation/gambling/loss/limitation/details',
          method: 'GET',
          enrichRequestData: false,
        }),
      transformResponse: transformGamblingLimitationDetailsResponse,
      rawResponseSchema: gamblingLimitationDetailsResponseSchema,
    }),
    lossLimitationTypes: build.query<
      TransformedLossLimitationTypesResponse,
      void
    >({
      query: () =>
        makeSelectAuthApiConfigWithStore({
          url: '/responsible-gaming/v1/self-limitation/gambling/loss/limitation/types',
          method: 'GET',
          enrichRequestData: false,
        }),
      transformResponse: transformLossLimitationTypesResponse,
      rawResponseSchema: lossLimitationTypesResponseSchema,
    }),
    selfExclusionTypes: build.query<SelfExclusionTypesIResponse, void>({
      query: () =>
        makeSelectAuthApiConfigWithStore({
          url: '/responsible-gaming/v1/self-exclusion/types',
          method: 'GET',
          enrichRequestData: false,
        }),
    }),
    selfExclusion: build.query<TransformedSelfExclusionResponse, void>({
      query: () =>
        makeSelectAuthApiConfigWithStore({
          url: '/responsible-gaming/v1/self-exclusion/exclusions',
          method: 'GET',
          enrichRequestData: false,
        }),
      transformResponse: transformSelfExclusionResponse,
      rawResponseSchema: selfExclusionResponseSchema,
    }),
    topUpLimitation: build.query<TransformedTopUpLimitationResponse, void>({
      query: () =>
        makeSelectAuthApiConfigWithStore({
          url: '/responsible-gaming/v1/self-limitation/topup/limitation',
          method: 'GET',
          enrichRequestData: false,
        }),
      transformResponse: transformTopUpLimitationResponse,
      rawResponseSchema: topUpLimitationResponseSchema,
    }),
    sessionLossDetail: build.query<TransformedSessionLossDetailResponse, void>({
      query: () =>
        makeSelectAuthApiConfigWithStore({
          url: '/responsible-gaming/v1/self-limitation/gambling/loss/session/last',
          method: 'GET',
          enrichRequestData: false,
        }),
      transformResponse: transformSessionLossDetailResponse,
      rawResponseSchema: sessionLossDetailResponseSchema,
    }),
    accountLimitation: build.mutation<
      AccountLimitationIResponse,
      AccountLimitationRequest
    >({
      query: (data) =>
        makeSelectAuthApiConfigWithStore({
          url: '/responsible-gaming/v1/self-limitation/topup/limitation',
          method: 'POST',
          enrichRequestData: false,
          data,
        }),
      transformErrorResponse: transformAccountLimitationErrorResponse,
    }),
  }),
});

export const {
  useAccountLimitationTypesQuery,
  useAccountLimitationPendingQuery,
  useAutomaticWithdrawalLimitQuery,
  useGamblingLimitationDetailsQuery,
  useLossLimitationTypesQuery,
  useSelfExclusionTypesQuery,
  useSelfExclusionQuery,
  useTopUpLimitationQuery,
  useSessionLossDetailQuery,
  useAccountLimitationMutation,
} = enhancedApi;
